import React, { useState } from "react";
import { ButtonDropdown } from "@amzn/awsui-components-react";
import { Investigation, Stage } from "../../lusca-search-api/generated-src";
import { fetchAgentPrompt } from "../../hooks/useInvestigations";
import {
  InvestigationMergeAction,
  mergeBlocker,
} from "./InvestigationMergeAction";
import {
  InvestigationSplitAction,
  splitBlocker,
  splitListBlocker,
} from "./InvestigationSplitAction";
import { InvestigationSplitListModal } from "./InvestigationSplitListModal";
import { isTerminal } from "./investigationUtils";
import { isListPopulation } from "./statusHint";

interface InvestigationMoreActionsProps {
  stage: Stage;
  investigation: Investigation;
  currentUser?: string | null;
  onMerged?: () => void;
  /** "View in dashboard" entries (one per rule branch; the dashboard filter cannot express OR). */
  dashboardLinks?: Array<{ text: string; href: string; description?: string }>;
}

/**
 * The page header's overflow menu: Split by rule / by list, Merge and "Copy agent prompt" (for a local agent such as
 * Kiro; TrackStars has its own button). Keeps the header to a few primary actions on small screens.
 */
export const InvestigationMoreActions: React.FC<
  InvestigationMoreActionsProps
> = ({ stage, investigation, currentUser, onMerged, dashboardLinks = [] }) => {
  const [dialog, setDialog] = useState<"split" | "split-list" | "merge" | null>(
    null
  );
  const [copied, setCopied] = useState<"idle" | "copied" | "failed">("idle");
  const closed = isTerminal(investigation.status);
  const split = isListPopulation(investigation)
    ? "This investigation was split off by a list; split it by list instead."
    : splitBlocker(investigation, currentUser);
  const splitList = splitListBlocker(investigation, currentUser);
  const merge = mergeBlocker(investigation);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(
        await fetchAgentPrompt(stage, investigation.id)
      );
      setCopied("copied");
    } catch (err) {
      console.error(err);
      setCopied("failed");
    }
  };

  return (
    <>
      <ButtonDropdown
        ariaLabel="More actions"
        items={[
          ...dashboardLinks.map((link, i) => ({
            id: `dashboard-${i}`,
            text: link.text,
            description: link.description,
            href: link.href,
            external: true,
          })),
          ...(closed
            ? []
            : [
                {
                  id: "split",
                  text: "Split by rule",
                  iconName: "treeview-expand" as const,
                  disabled: !!split,
                  disabledReason: split,
                },
                {
                  id: "split-list",
                  text: "Split by list",
                  description: "Move listed DLQ keys to a new case",
                  iconName: "list-view" as const,
                  disabled: !!splitList,
                  disabledReason: splitList,
                },
                {
                  id: "merge",
                  text: "Merge",
                  iconName: "shrink" as const,
                  disabled: !!merge,
                  disabledReason: merge ? `Cannot merge: ${merge}.` : undefined,
                },
              ]),
          {
            id: "prompt",
            text:
              copied === "copied"
                ? "Agent prompt copied"
                : copied === "failed"
                ? "Copy failed — try again"
                : "Copy agent prompt",
            iconName: "copy" as const,
          },
        ]}
        onItemClick={({ detail }) => {
          if (detail.id === "split") setDialog("split");
          else if (detail.id === "split-list") setDialog("split-list");
          else if (detail.id === "merge") setDialog("merge");
          else if (detail.id === "prompt") copyPrompt().catch(console.error);
        }}
      >
        More
      </ButtonDropdown>
      {dialog === "split" && (
        <InvestigationSplitAction
          stage={stage}
          investigation={investigation}
          currentUser={currentUser}
          open
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "split-list" && (
        <InvestigationSplitListModal
          stage={stage}
          investigation={investigation}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "merge" && (
        <InvestigationMergeAction
          stage={stage}
          investigation={investigation}
          open
          onClose={() => setDialog(null)}
          onMerged={() => {
            setDialog(null);
            onMerged?.();
          }}
        />
      )}
    </>
  );
};

export default InvestigationMoreActions;
